import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import DocumentHead from "@/components/DocumentHead";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Check, X, ArrowRight, Shield, Zap, AtSign, Mail,
  Layers, Forward, Star, Headphones, Crown, ChevronDown, ChevronUp, Users, Eye,
} from "lucide-react";

const COMPARISON = [
  { feature: "Anonymous aliases", free: "1", pro: "Unlimited", icon: AtSign },
  { feature: "Inbox access", free: true, pro: true, icon: Mail },
  { feature: "Read, archive, delete", free: true, pro: true, icon: Zap },
  { feature: "Categories & labels", free: false, pro: true, icon: Layers },
  { feature: "VIP contacts", free: false, pro: true, icon: Star },
  { feature: "Batch actions", free: false, pro: true, icon: Zap },
  { feature: "Email forwarding", free: false, pro: true, icon: Forward },
  { feature: "Admin dashboard", free: false, pro: true, icon: Shield },
  { feature: "Priority support", free: false, pro: true, icon: Headphones },
];

// PROMPT 032 — One clean positive-only bullet list + subline per plan.
// No ✕, no negatives, no comparison logic. Four bullets max, plus one
// quiet subline. Keyed by `plan.key` (backend returns pro_daily /
// pro_monthly / pro_yearly); generic fallback used otherwise.
const PLAN_CONTENT = {
  pro_daily: {
    subline: "Perfect for one-time use",
    bullets: [
      "Keep your address for 24 hours",
      "1 additional alias",
      "Send & receive emails anonymously",
      "No personal email required",
    ],
  },
  pro_monthly: {
    subline: "Best for regular use",
    bullets: [
      "Keep your address active",
      "3 additional aliases",
      "Send & receive emails anonymously",
      "No personal email required",
    ],
  },
  pro_yearly: {
    subline: "Best value",
    bullets: [
      "Keep your address long-term",
      "7 additional aliases",
      "Send & receive emails anonymously",
      "No personal email required",
    ],
  },
};

const FAQ = [
  {
    q: "Can I try before I pay?",
    a: "Yes — start a 30-minute trial inbox right from the homepage. No account, no credit card. The address you use during the trial can be kept by choosing any plan above.",
  },
  {
    q: "What happens to my trial address when I upgrade?",
    a: "It comes with you. Pick a plan, complete checkout, and your trial address is locked to your new account. No emails are lost.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Cancel with one click in Settings. You keep Pro access until the end of your billing period. No questions asked.",
  },
  {
    q: "Is my data private?",
    a: "Your aliases are not tied to your identity, and you are in full control of how you use them. You can create custom alias names and manage them independently. We ensure strong isolation between users. No tracking, no ads.",
  },
  {
    q: "What payment methods do you accept?",
    a: "We use Stripe for secure payments. All major credit cards, debit cards, and Apple Pay / Google Pay are supported.",
  },
];

// ─── Experiment assignment helpers ───────────────────

const ASSIGNMENT_KEY = "pricing_experiment_assignment";

function getStoredAssignment() {
  try {
    const raw = localStorage.getItem(ASSIGNMENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function assignVariant(experiment) {
  // Sticky: return existing if same experiment
  const stored = getStoredAssignment();
  if (stored && stored.experiment_id === experiment.experiment_id) {
    return stored.variant;
  }
  // New assignment based on traffic_split (% to A)
  const roll = Math.random() * 100;
  const variant = roll < experiment.traffic_split ? "A" : "B";
  const assignment = { experiment_id: experiment.experiment_id, variant };
  localStorage.setItem(ASSIGNMENT_KEY, JSON.stringify(assignment));
  return variant;
}

function clearAssignment() {
  localStorage.removeItem(ASSIGNMENT_KEY);
}


export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const previewKey = searchParams.get("preview");

  // PROMPT 030 — Pull the prefilled alias choice from URL or the inbox-
  // side hint. Order: URL (?keep_alias=) wins → localStorage (set by
  // FreeAliasPage's "Switch & keep" / "Secure your address" CTAs).
  // Strictly read-only on this page; the actual alias reservation will
  // be wired into Stripe metadata in a future Phase B.
  const keepAliasLocalPart = (() => {
    const fromUrl = (searchParams.get("keep_alias") || "").trim().toLowerCase();
    if (fromUrl) return fromUrl.replace(/[^a-z0-9._-]/g, "");
    try {
      const fromLs = (localStorage.getItem("qib_pending_keep_alias") || "").trim().toLowerCase();
      return fromLs.replace(/[^a-z0-9._-]/g, "");
    } catch { return ""; }
  })();
  const keepAliasAddress = keepAliasLocalPart ? `${keepAliasLocalPart}@mail.quickinbox.app` : "";

  // PROMPT 030 — Quiet trial-hold countdown. Reads the alias's
  // claim_code_expires_at as ISO from localStorage (set by FreeAliasPage
  // when the user clicks Secure / Switch & keep). Re-renders every
  // second; <=0 surfaces a tiny "Session expired" line instead. No
  // pressure copy, no scarcity nudges — just honest "this is held for
  // you" framing.
  const [holdNow, setHoldNow] = useState(() => Date.now());
  useEffect(() => {
    if (!keepAliasAddress) return;
    const t = setInterval(() => setHoldNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [keepAliasAddress]);
  const holdUntilMs = (() => {
    try {
      const iso = localStorage.getItem("qib_pending_keep_alias_until");
      if (!iso) return 0;
      const ms = new Date(iso).getTime();
      return Number.isFinite(ms) ? ms : 0;
    } catch { return 0; }
  })();
  const holdRemainingMs = holdUntilMs ? holdUntilMs - holdNow : 0;
  const holdMinutes = Math.max(0, Math.floor(holdRemainingMs / 60000));
  const holdSeconds = Math.max(0, Math.floor((holdRemainingMs % 60000) / 1000));
  const holdActive = holdUntilMs > 0 && holdRemainingMs > 0;
  const holdExpired = holdUntilMs > 0 && holdRemainingMs <= 0;
  const holdMmSs = `${String(holdMinutes).padStart(2, "0")}:${String(holdSeconds).padStart(2, "0")}`;
  const [activePricing, setActivePricing] = useState(null);
  const [planStatus, setPlanStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [isPreview, setIsPreview] = useState(false);
  const [experiment, setExperiment] = useState(null); // { experiment_id, variant }
  const [ready, setReady] = useState(false); // flicker prevention

  useEffect(() => {
    const fetchData = async () => {
      try {
        let pricingRes;

        // Priority 1: Preview mode (ignores experiments completely)
        if (previewKey) {
          try {
            pricingRes = await api.get(`/pricing/preview/${previewKey}`);
            setIsPreview(true);
          } catch {
            pricingRes = await api.get("/pricing/active");
            setIsPreview(false);
          }
          setActivePricing(pricingRes.data);
          setExperiment(null);
          clearAssignment();
        } else {
          // Priority 2: Check for active experiment
          let expData = null;
          try {
            const expRes = await api.get("/experiments/active");
            expData = expRes.data.experiment;
          } catch { /* no experiment */ }

          if (expData) {
            const variant = assignVariant(expData);
            const expInfo = { experiment_id: expData.experiment_id, variant };
            setExperiment(expInfo);

            // Fetch pricing for the assigned variant
            try {
              pricingRes = await api.get(`/experiments/pricing/${variant}`);
            } catch {
              // Fallback: use normal pricing if variant model not found
              pricingRes = await api.get("/pricing/active");
              setExperiment(null);
              clearAssignment();
            }
          } else {
            // Priority 3: Normal pricing logic
            pricingRes = await api.get("/pricing/active");
            setExperiment(null);
            clearAssignment();
          }
          setIsPreview(false);
          setActivePricing(pricingRes.data);
        }

        if (user) {
          try {
            const statusRes = await api.get("/payments/plan-status");
            setPlanStatus(statusRes.data);
          } catch { /* ignore */ }
        }
      } catch {
        toast.error("Failed to load plans");
      } finally {
        setReady(true);
      }
    };
    fetchData();
  }, [user, previewKey]);

  // Track experiment view (once per session)
  useEffect(() => {
    if (!experiment || !ready) return;
    const sessionKey = `exp_view_${experiment.experiment_id}`;
    if (sessionStorage.getItem(sessionKey)) return;

    api.post("/experiments/track", {
      experiment_id: experiment.experiment_id,
      variant: experiment.variant,
      event_type: "view",
    }).catch(() => {});

    sessionStorage.setItem(sessionKey, "1");
  }, [experiment, ready]);

  const handleUpgrade = async (planKey, stripePriceId) => {
    // PROMPT 033 — defensive guards. Previously, an empty stripePriceId
    // bubbled all the way to the backend as `null`, which Stripe
    // rejected with a 500. Now we fail fast with a friendly toast and
    // never make the network request. We also tolerate an unknown plan
    // key — fall back to whatever yearly plan exists in `plans`.
    if (!planKey) {
      toast.error("Plan not available right now. Please refresh.");
      return;
    }
    // Track experiment click
    if (experiment) {
      api.post("/experiments/track", {
        experiment_id: experiment.experiment_id,
        variant: experiment.variant,
        event_type: "click",
      }).catch(() => {});
    }

    setLoading(true);
    try {
      const originUrl = window.location.origin;

      if (user) {
        // Authenticated checkout (existing flow)
        const checkoutBody = { plan_id: planKey, origin_url: originUrl };
        if (experiment) {
          checkoutBody.experiment_id = experiment.experiment_id;
          checkoutBody.experiment_variant = experiment.variant;
        }
        const { data } = await api.post("/payments/checkout", checkoutBody);
        if (!data?.url) throw new Error("Checkout URL missing from server response");
        window.location.href = data.url;
      } else {
        // Guest PRO checkout — pass stripe_price_id directly
        if (!stripePriceId) {
          toast.error("This plan isn't ready yet. Please pick another or try again in a minute.");
          return;
        }
        const { data } = await api.post("/payments/pro-checkout", {
          stripe_price_id: stripePriceId,
          origin_url: originUrl,
        });
        if (!data?.url) throw new Error("Checkout URL missing from server response");
        window.location.href = data.url;
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // PROMPT 033 — never surface a raw 500 / "Internal Server Error".
      // Always show a calm, retry-friendly message.
      const msg = typeof detail === "string" && detail
        ? detail
        : "We couldn't start checkout. Please try again in a moment.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const isPaid = planStatus?.is_pro;
  const plans = activePricing?.plans || [];
  const pricingModel = activePricing?.model;

  // Flicker prevention: don't render until data is resolved
  if (!ready) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="pricing-page-loading">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="pricing-page">
      <DocumentHead
        title="QuickInbox Pricing — Free & Pro Plans"
        description="Choose a plan and get full anonymous email access — 1-day pass, monthly, or yearly. Try the 30-minute trial from the homepage. No free account required."
        path="/pricing"
      />
      {/* Preview Banner (admin-only) */}
      {isPreview && pricingModel && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center justify-center gap-3" data-testid="pricing-preview-banner">
          <Eye size={15} className="text-amber-600 shrink-0" />
          <p className="text-xs text-amber-800">
            <span className="font-medium">Preview mode</span> — Viewing{" "}
            <code className="font-mono bg-amber-100 px-1.5 py-0.5 rounded text-[11px]">{pricingModel.key}</code>{" "}
            ({pricingModel.name})
            {pricingModel.status !== "active" && (
              <Badge className="ml-2 text-[9px] bg-amber-100 text-amber-600 border-amber-300" variant="outline">{pricingModel.status}</Badge>
            )}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="h-6 text-[10px] border-amber-300 text-amber-600 hover:bg-amber-100"
            onClick={() => navigate("/pricing")}
            data-testid="pricing-preview-exit"
          >
            Exit preview
          </Button>
        </div>
      )}

      {/* Header — matches Landing (PROMPT 032). Same red QuickInbox
          logo, same brand wordmark, same navigation so the user never
          feels they hopped into a different product. */}
      <header className="border-b border-[#E5E5E5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <button onClick={() => navigate(user ? "/inbox" : "/")} className="flex items-center gap-2" data-testid="pricing-logo">
            <img
              src="/logo-quickinbox.png"
              alt="QuickInbox"
              width="28"
              height="28"
              className="w-7 h-7 rounded-md object-cover"
              loading="eager"
            />
            <span className="font-heading text-lg font-medium tracking-tight text-[#111111]">QuickInbox</span>
          </button>
          {!user && (
            <Button variant="ghost" size="sm" onClick={() => navigate("/login")} className="text-zinc-500 text-sm">
              Sign in
            </Button>
          )}
        </div>
      </header>

      <main>
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-10">
        <div className="text-center">
          <Badge variant="outline" className="text-xs font-mono mb-4 text-blue-600 border-blue-200">PRICING</Badge>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium text-[#111111]" data-testid="pricing-title">
            Protect your identity.
            <br />
            Send emails anonymously.
          </h1>

          {/* PROMPT 030 — Prominent address display. Shown when the user
              came in with a pending alias choice (?keep_alias=… on the
              URL OR localStorage.qib_pending_keep_alias set from the
              inbox). The address is the visual anchor of the pricing
              page — no marketing, no buttons, just confirmation. */}
          {keepAliasAddress && (
            <>
              <div className="mt-7 inline-flex items-center gap-3 px-5 py-3 rounded-xl border border-emerald-200 bg-emerald-50/70 shadow-sm" data-testid="pricing-keep-alias-display">
                <span
                  className="font-mono text-base sm:text-lg text-[#111111] break-all sm:break-normal"
                  data-testid="pricing-keep-alias-address"
                >
                  {keepAliasAddress}
                </span>
                <span
                  className="shrink-0 w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center"
                  aria-label="Address available"
                  data-testid="pricing-keep-alias-check"
                >
                  <Check size={15} className="text-white" strokeWidth={3} />
                </span>
              </div>

              {/* PROMPT 030 — quiet hold countdown. Calm, systemic copy
                  by design — no urgency nudges, no scarcity pressure. */}
              {holdActive && (
                <p
                  className="mt-2 text-xs text-emerald-700/90 tabular-nums"
                  aria-live="polite"
                  data-testid="pricing-keep-alias-hold"
                >
                  Reserved for you — <span className="font-mono">{holdMmSs}</span> remaining
                </p>
              )}
              {holdExpired && (
                <p
                  className="mt-2 text-xs text-zinc-500"
                  data-testid="pricing-keep-alias-hold-expired"
                >
                  Session expired
                </p>
              )}
            </>
          )}

          {/* Social Proof */}
          <div className="flex items-center justify-center gap-4 mt-6" data-testid="social-proof">
            <div className="flex -space-x-2">
              {["bg-blue-500", "bg-green-500", "bg-amber-500", "bg-purple-500", "bg-red-400"].map((c, i) => (
                <div key={i} className={`w-7 h-7 rounded-full border-2 border-zinc-200 ${c} flex items-center justify-center text-[9px] font-medium text-white`}>
                  {String.fromCharCode(65 + i)}
                </div>
              ))}
            </div>
            <p className="text-sm text-zinc-500">
              <span className="font-medium text-[#555555]">2,400+</span> aliases created
            </p>
          </div>
        </div>
      </section>

      {/* Plan Cards — purchase decisions only (no trial card here).
          The Trial lives on Landing + Inbox; Pricing is reached after
          that experience and must focus the user on choosing a paid
          plan to keep their address. */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-16">
        <div className={`grid grid-cols-1 gap-6 ${plans.length <= 2 ? "md:grid-cols-2 max-w-3xl mx-auto" : plans.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2 lg:grid-cols-3"}`}>
          {/* Dynamic Plans — uniform height via flex, CTA fixed bottom */}
          {plans.map((plan) => {
            const content = PLAN_CONTENT[plan.key] || {
              subline: plan.description || "",
              bullets: [
                "Keep your address",
                "Send & receive emails anonymously",
                "No personal email required",
                "No tracking",
              ],
            };
            // PROMPT 032 — unified badge. Only two labels allowed:
            // "Most popular" on the monthly (highlighted) plan,
            // "Best value" on yearly. Everything else → no badge.
            let badgeText = "";
            if (plan.is_highlighted) badgeText = "Most popular";
            else if (plan.key === "pro_yearly") badgeText = "Best value";
            return (
              <div
                key={plan.plan_id}
                className={`p-6 sm:p-8 rounded-xl bg-white relative flex flex-col h-full ${plan.is_highlighted ? "border-2 border-[#E11D2E]" : "border border-[#E5E5E5]"}`}
                data-testid={`plan-${plan.key}`}
              >
                {badgeText && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span
                      className={`inline-block text-[10px] tracking-wide px-2.5 py-1 rounded-full border font-medium ${plan.is_highlighted ? "bg-[#E11D2E] text-white border-[#E11D2E]" : "bg-white text-[#111111] border-[#E5E5E5]"}`}
                      data-testid={`plan-${plan.key}-badge`}
                    >
                      {badgeText}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2 mb-4">
                  <Crown size={20} className={plan.is_highlighted ? "text-[#E11D2E]" : "text-zinc-500"} />
                  <h3 className="font-heading text-lg font-medium text-[#111111]">{plan.name}</h3>
                </div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="font-heading text-4xl font-medium text-[#111111]" data-testid={`plan-${plan.key}-price`}>
                    ${plan.price}
                  </span>
                  <span className="text-sm text-zinc-500">
                    /{plan.duration_value > 1 ? `${plan.duration_value} ` : ""}{plan.duration_unit}{plan.duration_value > 1 ? "s" : ""}
                  </span>
                </div>
                {plan.compare_at_price && plan.price < plan.compare_at_price && (
                  <p className="text-xs text-green-600 font-medium mb-1" data-testid={`plan-${plan.key}-savings`}>
                    <span className="line-through text-zinc-500 mr-1">${plan.compare_at_price}</span>
                    Save {Math.round((1 - plan.price / plan.compare_at_price) * 100)}%
                  </p>
                )}
                {content.subline && (
                  <p
                    className="text-xs text-zinc-500 mt-2 mb-5"
                    data-testid={`plan-${plan.key}-subline`}
                  >
                    {content.subline}
                  </p>
                )}
                <Separator className="mb-6" />
                <ul className="space-y-3" data-testid={`plan-${plan.key}-features`}>
                  {content.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-center gap-2.5 text-sm font-medium text-[#555555]"
                    >
                      <Check
                        size={15}
                        className={`shrink-0 ${plan.is_highlighted ? "text-[#E11D2E]" : "text-emerald-600"}`}
                        strokeWidth={2.5}
                      />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                {/* Spacer pushes the CTA to the bottom so every card
                    has its button on the same line regardless of bullet
                    count differences. */}
                <div className="flex-1 min-h-6" />
                {isPaid ? (
                  <Button disabled className="w-full h-11 bg-green-50 text-green-600 border-green-200" data-testid={`plan-${plan.key}-active`}>
                    <Check size={16} className="mr-2" /> Active
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleUpgrade(plan.key, plan.stripe_price_id)}
                    disabled={loading}
                    className="w-full h-11 font-medium bg-[#E11D2E] hover:bg-[#C8102B] text-white"
                    data-testid={`plan-${plan.key}-cta`}
                  >
                    {loading ? "Redirecting..." : "Start now"}
                    {!loading && <ArrowRight size={16} className="ml-2" />}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* Global Trust / Privacy box (PROMPT — pricing matrix).
            Plan-agnostic anchor, sits below the plan grid. Contains
            ZERO plan differentiation — same promise for every paid
            tier. Calm visual: soft emerald border, generous padding,
            quiet checkmarks. */}
        <div
          className="mt-10 rounded-xl border border-emerald-200/70 bg-emerald-50/40 p-6 sm:p-8 text-center"
          data-testid="trust-privacy-box"
        >
          <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-emerald-500/10 mb-4">
            <Shield size={20} className="text-emerald-600" strokeWidth={2.25} />
          </div>
          <h3
            className="font-heading text-xl sm:text-2xl tracking-tight font-medium text-[#111111]"
            data-testid="trust-privacy-headline"
          >
            Your identity stays protected. Always.
          </h3>
          <ul
            className="mt-5 inline-grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 text-sm text-[#333333] text-left"
            data-testid="trust-privacy-bullets"
          >
            {[
              "No personal email required",
              "No tracking. No profiling",
              "No identity leakage",
              "Anonymous by design",
            ].map((line) => (
              <li key={line} className="flex items-center gap-2.5">
                <Check size={14} className="shrink-0 text-emerald-600" strokeWidth={2.5} />
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <p
            className="mt-5 text-xs text-zinc-500 max-w-md mx-auto"
            data-testid="trust-privacy-subline"
          >
            Built for privacy-first communication — not marketing, not data collection.
          </p>
        </div>

        {/* Trust Signal */}
        <p className="text-center text-xs text-zinc-500 mt-4" data-testid="trust-signal">
          Cancel anytime. No hidden fees. Secure checkout via Stripe.
        </p>
      </div>

      {/* Feature Comparison section removed (PROMPT 032) — the pricing
          page is no longer a comparison tool, just a clean "which
          length do I want?" decision. The per-plan positive bullets on
          the cards above + the global trust box carry all the info. */}

      {/* Why anonymous email? */}
      <section className="border-t border-[#E5E5E5] bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl tracking-tight font-medium text-[#111111] mb-2" data-testid="why-section-title">
              Why use an anonymous email?
            </h2>
            <p className="text-sm text-zinc-500">Real problems this solves — every day.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="why-section">
            {[
              { q: "Signing up for a free trial?", a: "Use an alias. If they spam you later, just delete it." },
              { q: "Selling something online?", a: "Give buyers an alias, not your real email. Cut contact when you're done." },
              { q: "Testing a new app or service?", a: "Don't trust them yet? Use a disposable alias first." },
              { q: "Getting too much marketing email?", a: "Create rules to auto-delete or forward only what matters." },
              { q: "Want separate inboxes?", a: "One alias for shopping, one for newsletters, one for work. You decide." },
              { q: "Worried about data breaches?", a: "If a service leaks your alias, your real email stays safe." },
            ].map(({ q, a }, i) => (
              <div key={i} className="p-4 rounded-xl bg-white border border-[#E5E5E5]">
                <p className="text-sm font-medium text-[#555555]">{q}</p>
                <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-[#E5E5E5]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
          <h2 className="font-heading text-2xl tracking-tight font-medium text-[#111111] text-center mb-8" data-testid="faq-title">
            Frequently asked questions
          </h2>
          <div className="space-y-2" data-testid="faq-section">
            {FAQ.map((item, i) => (
              <div key={i} className="border border-[#E5E5E5] rounded-lg overflow-hidden" data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-4 sm:px-5 py-4 text-left hover:bg-[#F5F5F5] transition-colors"
                >
                  <span className="text-sm font-medium text-[#111111] pr-4">{item.q}</span>
                  {openFaq === i ? (
                    <ChevronUp size={16} className="text-zinc-500 shrink-0" />
                  ) : (
                    <ChevronDown size={16} className="text-zinc-500 shrink-0" />
                  )}
                </button>
                {openFaq === i && (
                  <div className="px-4 sm:px-5 pb-4">
                    <p className="text-sm text-[#888888] leading-relaxed">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-[#E5E5E5] bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center" data-testid="final-cta">
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-[#111111] mb-3">
            Ready to take control of your privacy?
          </h2>
          <p className="text-base text-zinc-500 max-w-md mx-auto mb-8">
            Pick a plan above. Cancel anytime. Your data stays private.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            {!isPaid && (
              <Button onClick={() => {
                const yearly = plans.find(p => p.key === "pro_yearly") || plans[0];
                if (yearly) handleUpgrade(yearly.key, yearly.stripe_price_id);
              }} disabled={loading} className="h-11 px-6 bg-[#E11D2E] hover:bg-[#C8102B] text-white" data-testid="final-cta-pro">
                {loading ? "..." : "Start now"} <ArrowRight size={16} className="ml-2" />
              </Button>
            )}
          </div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#E5E5E5] py-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <p className="text-xs text-zinc-500 font-mono">QuickInbox v1.0</p>
          <div className="flex items-center gap-4 text-xs text-zinc-500">
            <button onClick={() => navigate("/login")} className="hover:text-[#555555]">Sign in</button>
            <button onClick={() => navigate("/claim")} className="hover:text-[#555555]">Claim alias</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
